import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { GeneratorForm } from "../components/GeneratorForm";
import { ProjectCard } from "../components/ProjectCard";
import { YoutubeConnectCard } from "../components/YoutubeConnectCard";
import { api } from "../lib/api";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All", testid: "project-filter-all" },
  { key: "completed", label: "Completed", testid: "project-filter-completed" },
];

const activeStatuses = ["pending", "script", "voiceover", "visuals", "assembly"];

export default function DashboardPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const res = await api.get("/projects");
      setProjects(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      setProjects((prev) => {
        if (prev.some((p) => activeStatuses.includes(p.status))) {
          load();
        }
        return prev;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleCreated = (id) => navigate(`/project/${id}`);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error("Failed to delete project");
    }
  };

  const videoProjects = projects.filter((p) => p.kind !== "song");
  const filtered = filter === "completed" ? videoProjects.filter((p) => p.status === "completed") : videoProjects;

  return (
    <div className="min-h-screen">
      <Header onNewProject={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        <YoutubeConnectCard slot="video" label="Video Maker" />
        <GeneratorForm onCreated={handleCreated} />

        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-50">Your Videos</h2>
            <div className="flex gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={f.testid}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-mono transition-all ${
                    filter === f.key ? "bg-amber-500 text-black" : "bg-slate-800/80 text-slate-400"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {filtered.length === 0 ? (
            <p className="text-slate-500 text-sm">No videos yet. Generate your first one above!</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {filtered.map((p, idx) => (
                <div key={p.id} style={{ animationDelay: `${idx * 0.05}s` }} className="animate-in fade-in">
                  <ProjectCard project={p} onOpen={(id) => navigate(`/project/${id}`)} onDelete={handleDelete} />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
